import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { fmtCAD } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { t } = useI18n();
  const nav = useNavigate();

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <ShoppingBag size={48} className="mx-auto text-gray-300" />
        <h1 className="font-heading text-3xl font-extrabold mt-6">{t("cart.empty")}</h1>
        <p className="text-gray-500 mt-3">{t("cart.emptySub")}</p>
        <Link to="/boutique" className="inline-block mt-8 bg-brand-red text-white px-8 py-4 font-semibold hover:bg-[#b81c14] transition-colors" data-testid="cart-empty-shop">{t("cart.shopBtn")}</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <h1 className="font-heading text-4xl font-extrabold tracking-tight mb-10">{t("cart.title")}</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="border border-gray-200 bg-white divide-y divide-gray-200">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4 p-4" data-testid={`cart-item-${it.product_id}`}>
              <div className="h-24 w-24 bg-gray-50 border border-gray-200 shrink-0 flex items-center justify-center">
                {it.image && <img src={it.image} alt={it.title} className="max-h-full max-w-full object-contain p-2" />}
              </div>
              <div className="flex-1">
                <span className="text-[11px] uppercase tracking-widest text-brand-red font-semibold">{it.brand}</span>
                <h3 className="text-sm font-medium line-clamp-2">{it.title}</h3>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex items-center border border-gray-300 text-sm">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-3 py-1">−</button>
                    <span className="px-3">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-3 py-1">+</button>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="text-gray-400 hover:text-red-500" data-testid={`cart-remove-${it.product_id}`}><Trash2 size={17} /></button>
                </div>
              </div>
              <div className="text-right font-semibold">{fmtCAD(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>

        <aside className="lg:sticky lg:top-28 self-start border border-gray-200 bg-white p-6 h-fit">
          <h2 className="font-heading text-xl font-bold mb-5">{t("cart.summary")}</h2>
          <div className="flex justify-between text-sm py-2"><span className="text-gray-500">{t("cart.subtotal")}</span><span className="font-semibold" data-testid="cart-subtotal">{fmtCAD(subtotal)}</span></div>
          <div className="flex justify-between text-sm py-2 border-b border-gray-200"><span className="text-gray-500">{t("cart.taxShip")}</span><span className="text-gray-400">{t("cart.calc")}</span></div>
          <div className="flex justify-between py-4 text-lg font-bold"><span>{t("cart.total")}</span><span>{fmtCAD(subtotal)}</span></div>
          <button onClick={() => nav("/commande")} className="w-full flex items-center justify-center gap-2 bg-brand-red text-white font-semibold py-4 hover:bg-[#b81c14] transition-colors" data-testid="cart-checkout-btn">
            {t("cart.checkout")} <ArrowRight size={18} />
          </button>
          <Link to="/boutique" className="block text-center text-sm text-gray-500 mt-4 hover:text-brand-red">{t("cart.continue")}</Link>
        </aside>
      </div>
    </div>
  );
}
